import { FC, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { NAV_ROUTES } from '../../constants/navRoutes';
import { useAuthenticateMutation } from '../../services/api';
import backgroundImage from '../../assets/images/pvx-home-bg.png';
import UserAuth from '../../services/auth';
import activityTracker from '../../services/activityLogger';
import { setAuthToken } from '../../stores/App/slice';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, SESSION_STORAGE_KEYS } from '@constants/constants';
import { useAddUserPreferenceMutation } from '@api/userDetails';

const AuthRedirect: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t: translations } = useTranslation();

  const [setPreference] = useAddUserPreferenceMutation();
  const [authenticate] = useAuthenticateMutation();

  const updatePreference = async (language: string) => {
    try {
      const languageCode = LANGUAGES.find((lang) => lang.locale === language)?.code || 'EN';
      await setPreference({ language: languageCode }).unwrap();
    } catch (e) {
      console.error('Failed to update user language preference:', e);
    }
  };

  const parseAccessToken = (token: string) => {
    const parts = token.split('.');

    // Ensure the token has three parts
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token');
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    // Decode the payload as UTF-8
    const decoder = new TextDecoder('utf-8');
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const payload = decoder.decode(bytes);

    try {
      return JSON.parse(payload);
    } catch (error) {
      throw new Error('Invalid JWT payload');
    }
  };

  const authenticateKeycloak = async (code: string) => {
    try {
      const response = await authenticate({ code }).unwrap();
      const { access_token, refresh_token } = response || {};

      if (access_token && refresh_token) {
        dispatch(setAuthToken({ access_token, refresh_token }));
        const keyCloakLocale = parseAccessToken(access_token)?.locale || 'en';
        const storedLocale = localStorage.getItem('app_locale');
        if (storedLocale !== keyCloakLocale) {
          updatePreference(keyCloakLocale);
        }

        UserAuth.setTokens({ access_token, refresh_token });
        activityTracker.recordLogin(access_token);
      }
    } catch (error) {
      console.error('Error while authenticating user :', error);
    }
  };

  // Hook to fetch access token post keycloak authentication
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeParam = params.get('code');
    if (codeParam) {
      authenticateKeycloak(codeParam);
      setTimeout(() => {
        // Get the origin from session storage
        const originData = sessionStorage.getItem(SESSION_STORAGE_KEYS.ORIGIN);

        if (originData) {
          try {
            // Parse the stored origin data
            const { currentPath } = JSON.parse(originData);

            // Remove the origin from session storage
            sessionStorage.removeItem(SESSION_STORAGE_KEYS.ORIGIN);

            // Redirect to the origin path
            navigate(currentPath);
          } catch (error) {
            console.error('Error parsing origin data:', error);
            // If parsing fails, redirect to home page
            navigate(NAV_ROUTES.HOME);
          }
        } else {
          // If no origin exists, redirect to the home page
          navigate(NAV_ROUTES.HOME);
        }
      }, 1000);
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FFFFFF',
          textAlign: 'center',
        }}
      >
        <div>
          <p>{translations('common.authenticating')}</p>
        </div>
      </Box>
    </>
  );
};

export default AuthRedirect;
